import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { startOfMonth, endOfMonth, subMonths, startOfYear, format, parseISO, differenceInDays, subDays, eachMonthOfInterval, endOfDay, isSameMonth, isBefore, isAfter } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { useAppSelector } from '@/store/hooks';
import { getClosedMonths, getLastClosedMonth, filterClosedMonths } from '@/utils/closedMonthsUtils';

export interface ImpactFilters {
  dateRange: { from: Date; to: Date };
  establishments: string[];
  materials: string[];
  impactType: string;
  destinationType: string; // Reciclagem, Compostagem, Coprocessamento, Aterro
  wasteType: string; // Orgânicos, Recicláveis, Rejeitos, etc. (tipo_impacto)
  groupBy: 'month' | 'material' | 'unit';
  generators: string[];
}

export interface ImpactKPI {
  id: string;
  label: string;
  value: number;
  unit: string;
  icon: string;
  change?: number;
  tooltip: string;
}

export interface MaterialSummary {
  material: string;
  weight: number;
  percentage: number;
  icon: string;
  color: string;
}

export interface EvolutionDataPoint {
  period: string;
  co2: number;
  energia: number;
  material: number;
  aterro: number;
}

export interface ImpactByMaterial {
  material: string;
  co2: number;
  energy: number;
  recovered: number;
}

export interface UnitRanking {
  unit: string;
  value: number;
  co2: number;
}

// Interface para dados da VIEW
interface ImpactMetricRow {
  collect_id: string;
  planned_date: string;
  mes_ref: string;
  establishment_name: string | null;
  establishment_id: string;
  generator_id: string;
  kind_of_material: string | null;
  tipo_impacto: string;
  units_qty: number;
  massa_kg: number;
  mat_recuperado: number;
  energia_gerada: number;
  co2_evitado: number;
  espaco_aterro: number;
  km_carro: number;
  num_geladeiras: number;
}

const getDefaultFilters = (isInternal: boolean = false, lastClosedMonth: Date | null = null): ImpactFilters => {
  // Se não houver mês fechado, usar um range padrão (últimos 12 meses, excluindo o atual)
  let fromDate: Date;
  let toDate: Date;

  if (lastClosedMonth) {
    // Usar o último mês fechado como padrão
    fromDate = startOfMonth(lastClosedMonth);
    toDate = endOfMonth(lastClosedMonth);
  } else {
    // Fallback: último mês completo (excluindo o atual)
    const lastMonth = subMonths(new Date(), 1);
    fromDate = startOfMonth(lastMonth);
    toDate = endOfMonth(lastMonth);
  }

  return {
    dateRange: {
      from: fromDate,
      to: toDate
    },
    establishments: [],
    materials: [],
    impactType: 'all',
    destinationType: 'all', // Reciclagem, Compostagem, etc.
    wasteType: 'all', // Orgânicos, Recicláveis, etc. (tipo_impacto column)
    groupBy: 'month',
    generators: [],
  };
};

// Mapeamento de tipo de impacto (waste type) para labels e cores
const TIPO_IMPACTO_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  'Organico': { label: 'Orgânicos', icon: 'leaf', color: '#22C55E' },
  'Reciclavel_Misto': { label: 'Recicláveis', icon: 'recycle', color: '#10B981' },
  'Rejeito': { label: 'Rejeitos', icon: 'trash', color: '#EAB308' },
  'Oil': { label: 'Óleo', icon: 'droplet', color: '#3B82F6' },
  'RCC': { label: 'Entulho (RCC)', icon: 'building', color: '#8B5CF6' },
  'Sem_Regra': { label: 'Outros', icon: 'more-horizontal', color: '#9CA3AF' },
};

// Categorias exibidas no gráfico de distribuição por tipo de resíduo
// Cores seguem os tokens do design system Musa
const MATERIAL_CHART_CATEGORIES: Record<string, { label: string; color: string; order: number }> = {
  'Papel':      { label: 'Papel',      color: 'var(--primary-400)',   order: 1 },
  'Plástico':   { label: 'Plástico',   color: 'var(--secondary-400)', order: 2 },
  'Metal':      { label: 'Metal',      color: 'var(--neutral-400)',   order: 3 },
  'Reciclável': { label: 'Reciclável', color: 'var(--success-400)',   order: 4 },
  'Vidro':      { label: 'Vidro',      color: 'var(--info-400)',      order: 5 },
};

// Mapeia kind_of_material (slug do DB) para a categoria do gráfico
const KIND_OF_MATERIAL_TO_CATEGORY: Record<string, keyof typeof MATERIAL_CHART_CATEGORIES> = {
  'cardboard':    'Papel',
  'plastic':      'Plástico',
  'glass':        'Vidro',
  'metals':       'Metal',
  'scrap_metal':  'Metal',
  'ferrous_scrap':'Metal',
  'recyclable':   'Reciclável',
  'recycable':    'Reciclável', // typo variant in data
};

// Mapeamento de tipo de destinação para tipos de impacto
// Inferido logicamente: tipo de resíduo -> provável destinação final
const DESTINATION_TO_TIPO_IMPACTO: Record<string, string[]> = {
  'Reciclagem': ['Reciclavel_Misto'],
  'Compostagem': ['Organico'],
  'Coprocessamento': ['Oil', 'Rejeito'], // Óleo e rejeitos podem ir para coprocessamento
  'Aterro': ['Rejeito', 'RCC', 'Sem_Regra'], // Rejeitos e entulho vão para aterro
};

// Waste type options for the filter dropdown
const WASTE_TYPE_OPTIONS = [
  { value: 'Organico', label: 'Orgânicos' },
  { value: 'Reciclavel_Misto', label: 'Recicláveis' },
  { value: 'Rejeito', label: 'Rejeitos' },
  { value: 'Oil', label: 'Óleo' },
  { value: 'RCC', label: 'Entulho (RCC)' },
];


export const useImpactData = () => {
  const { profile } = useUnifiedAuth();
  const hasInitialized = useRef(false);
  const { establishments: filterEstablishments, materialOptions } = useFilterOptions();
  const rawMaterials = useAppSelector((s) => s.establishmentAccess.materials);
  /** Filtros armazenam material id; Supabase kind_of_material é nome — mapear id → nome */
  const materialNamesForQuery = useMemo(
    () =>
      (filters: ImpactFilters) =>
        (filters.materials?.map((id) => rawMaterials.find((m) => m.id === id)?.name).filter(Boolean) ?? []) as string[],
    [rawMaterials]
  );
  
  // Buscar meses fechados (apenas meses com faturamento encerrado; Supabase usa UUID)
  const { data: closedMonthsRaw = [], dataUpdatedAt } = useQuery({
    queryKey: ['closed-months', profile?.generator_id_legacy, profile?.is_internal],
    queryFn: async () => {
      const generatorId = profile?.is_internal ? undefined : (profile?.generator_id_legacy ?? undefined);
      return await getClosedMonths(generatorId ?? undefined);
    },
    enabled: !!profile,
    staleTime: 10 * 60 * 1000, // Cache por 10 minutos
  });


  // Garantir que closedMonths seja sempre um array de Date válidos
  // (React Query pode serializar/deserializar e converter Date para string)
  const closedMonths = useMemo(() => {
    const validDates = closedMonthsRaw
      .map(month => {
        if (month instanceof Date && !isNaN(month.getTime())) {
          return month;
        }
        if (typeof month === 'string') {
          const parsed = new Date(month);
          return !isNaN(parsed.getTime()) ? parsed : null;
        }
        return null;
      })
      .filter((date): date is Date => date !== null);
    
    
    return validDates;
  }, [closedMonthsRaw]);

  // Obter último mês fechado para usar como padrão
  const lastClosedMonth = useMemo(() => {
    return getLastClosedMonth(closedMonths);
  }, [closedMonths]);

  const [filters, setFilters] = useState<ImpactFilters>(() => 
    getDefaultFilters(profile?.is_internal === true, lastClosedMonth)
  );
  
  // Update filters when profile loads or closed months are available
  useEffect(() => {
    if (profile && closedMonths.length > 0 && !hasInitialized.current) {
      const defaultFilters = getDefaultFilters(profile.is_internal === true, lastClosedMonth);
      setFilters(prev => {
        // Só atualizar se ainda não foi inicializado ou se o range atual não é válido
        const currentMonth = startOfMonth(new Date());
        const isCurrentMonthInRange = 
          isSameMonth(currentMonth, startOfMonth(prev.dateRange.from)) ||
          isSameMonth(currentMonth, startOfMonth(prev.dateRange.to));
        
        if (isCurrentMonthInRange || !closedMonths.some(m => 
          isSameMonth(m, startOfMonth(prev.dateRange.from)) || 
          isSameMonth(m, startOfMonth(prev.dateRange.to))
        )) {
          return {
            ...prev,
            dateRange: defaultFilters.dateRange
          };
        }
        return prev;
      });
      hasInitialized.current = true;
    }
  }, [profile, closedMonths, lastClosedMonth]);

  // Calculate previous period dates
  const previousPeriod = useMemo(() => {
    const daysDiff = differenceInDays(filters.dateRange.to, filters.dateRange.from);
    return {
      from: subDays(filters.dateRange.from, daysDiff + 1),
      to: subDays(filters.dateRange.from, 1)
    };
  }, [filters.dateRange]);

  // Validar se um período contém apenas meses fechados
  const validateDateRange = useCallback((from: Date, to: Date): boolean => {
    if (closedMonths.length === 0) {
      return false;
    }
    
    const currentMonth = startOfMonth(new Date());
    const fromMonth = startOfMonth(from);
    const toMonth = startOfMonth(to);
    
    // Não permitir mês atual
    if (isSameMonth(fromMonth, currentMonth) || isSameMonth(toMonth, currentMonth)) {
      return false;
    }
    
    // Verificar se todos os meses no intervalo estão fechados
    const monthsInRange = eachMonthOfInterval({
      start: fromMonth,
      end: toMonth
    });
    
    return monthsInRange.every(month => {
      // Excluir mês atual
      if (isSameMonth(month, currentMonth)) {
        return false;
      }
      // Verificar se está na lista de meses fechados
      return closedMonths.some(closedMonth => isSameMonth(closedMonth, month));
    });
  }, [closedMonths]);

  // Helper to build query with filters
  const buildQuery = (startDate: string, endDate: string) => {
    let query = supabase
      .from('vw_impact_metrics')
      .select('*')
      .gte('planned_date', startDate)
      .lte('planned_date', endDate);

    // Apply generator filter (Supabase views use UUID)
    if (profile?.is_internal) {
      if (filters.generators?.length > 0) {
        query = query.in('generator_id', filters.generators);
      }
    } else if (profile?.generator_id_legacy && !profile?.is_internal) {
      query = query.eq('generator_id', profile.generator_id_legacy);
    }

    // Apply establishment filter
    if (filters.establishments.length > 0) {
      query = query.in('establishment_id', filters.establishments);
    }

    // Apply material filter (Supabase kind_of_material = nome; filtros guardam id)
    const materialNames = materialNamesForQuery(filters);
    if (materialNames.length > 0) {
      query = query.in('kind_of_material', materialNames);
    }

    // Apply waste type filter
    if (filters.wasteType && filters.wasteType !== 'all') {
      query = query.eq('tipo_impacto', filters.wasteType);
    }

    // Apply destination type filter (maps to tipo_impacto values)
    if (filters.destinationType && filters.destinationType !== 'all') {
      const tipoImpactoValues = DESTINATION_TO_TIPO_IMPACTO[filters.destinationType];
      if (tipoImpactoValues && tipoImpactoValues.length > 0) {
        query = query.in('tipo_impacto', tipoImpactoValues);
      }
    }

    return query;
  };

  // Fetch current period data
  const { data: impactData, isLoading } = useQuery({
    queryKey: [
      'impact-metrics', 
      format(filters.dateRange.from, 'yyyy-MM-dd'), 
      format(filters.dateRange.to, 'yyyy-MM-dd'),
      filters.establishments,
      filters.materials,
      filters.destinationType,
      filters.wasteType,
      filters.generators,
      profile?.generator_id_legacy
    ],
    queryFn: async () => {
      // Validar que o período contém apenas meses fechados antes de buscar dados
      if (closedMonths.length > 0) {
        const isValid = validateDateRange(filters.dateRange.from, filters.dateRange.to);
        if (!isValid) {
          return [];
        }
      }
      
      // Use endOfDay to ensure we include the entire last day
      const startDate = format(filters.dateRange.from, 'yyyy-MM-dd');
      const endDateStr = format(endOfDay(filters.dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      
      // Fetch all records with pagination (Supabase has a 1000 record limit by default)
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Query timeout after 30 seconds')), 30000);
      });

      const fetchPromise = async (): Promise<ImpactMetricRow[]> => {
        let allData: ImpactMetricRow[] = [];
        let page = 0;
        const pageSize = 1000;
        // Reduced limit - if we need more than 50k records, we should optimize the query
        const maxPages = 50; // 50,000 records max
        let hasMore = true;

        while (hasMore && page < maxPages) {
          const { data, error } = await buildQuery(startDate, endDateStr)
            .range(page * pageSize, (page + 1) * pageSize - 1);

          if (error) {
            console.error('❌ Error fetching impact data:', error);
            throw error;
          }

          if (data && data.length > 0) {
            allData = [...allData, ...data];
            hasMore = data.length === pageSize;
            page++;
          } else {
            hasMore = false;
          }
        }

        if (page >= maxPages && hasMore) {
          console.warn(`⚠️ Pagination limit reached at ${maxPages * pageSize} records. Some data may be missing.`);
        }

        return allData as ImpactMetricRow[];
      };

      try {
        return await Promise.race([fetchPromise(), timeoutPromise]);
      } catch (error) {
        if ((error as Error).message === 'Query timeout after 30 seconds') {
          console.error('❌ Query timeout - too much data. Consider reducing the date range.');
          throw new Error('A consulta demorou muito para carregar. Por favor, selecione um período menor.');
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!profile, // Sempre habilitar a query, a validação será feita dentro do queryFn
  });

  // Fetch previous period data for comparison
  // Disabled for internal users initially to improve performance (they can enable via filters later)
  const { data: previousPeriodData } = useQuery({
    queryKey: ['impact-metrics-previous', previousPeriod, filters.establishments, filters.materials, filters.destinationType, filters.wasteType, filters.generators, profile?.generator_id_legacy],
    queryFn: async () => {
      const startDate = format(previousPeriod.from, 'yyyy-MM-dd');
      const endDateStr = format(endOfDay(previousPeriod.to), 'yyyy-MM-dd HH:mm:ss');
      
      // Fetch all records with pagination (simpler for previous period - less critical)
      let allData: ImpactMetricRow[] = [];
      let page = 0;
      const pageSize = 1000;
      const maxPages = 50; // 50,000 records max
      let hasMore = true;

      try {
        while (hasMore && page < maxPages) {
          const { data, error } = await buildQuery(startDate, endDateStr)
            .range(page * pageSize, (page + 1) * pageSize - 1);

          if (error) {
            console.error('❌ Error fetching previous period data:', error);
            throw error;
          }

          if (data && data.length > 0) {
            allData = [...allData, ...data];
            hasMore = data.length === pageSize;
            page++;
          } else {
            hasMore = false;
          }
        }

        if (page >= maxPages && hasMore) {
          console.warn(`⚠️ Previous period pagination limit reached at ${maxPages * pageSize} records.`);
        }
      } catch (error) {
        console.error('❌ Error in previous period pagination:', error);
        throw error;
      }

      return allData as ImpactMetricRow[];
    },
    staleTime: 5 * 60 * 1000,
    // Only fetch previous period after main data loads (for performance)
    enabled: !!profile && !!impactData,
  });

  // Helper function to calculate percentage change
  const calculateChange = (current: number, previous: number): number | undefined => {
    if (previous === 0) return current > 0 ? 100 : undefined;
    return Math.round(((current - previous) / previous) * 100);
  };

  // Process real data from VIEW
  const data = useMemo(() => {
    try {
      if (!impactData || impactData.length === 0) {
        return {
          totalWeight: 0,
          materialSummary: [],
          kpis: [],
          evolutionData: [],
          impactByMaterial: [],
          unitRanking: [],
        };
      }

    // Aggregate totals from VIEW (REAL DATA from Metabase formulas)
    const totals = impactData.reduce((acc, row) => ({
      massa_kg: acc.massa_kg + Number(row.massa_kg || 0),
      mat_recuperado: acc.mat_recuperado + Number(row.mat_recuperado || 0),
      energia_gerada: acc.energia_gerada + Number(row.energia_gerada || 0),
      co2_evitado: acc.co2_evitado + Number(row.co2_evitado || 0),
      espaco_aterro: acc.espaco_aterro + Number(row.espaco_aterro || 0),
      km_carro: acc.km_carro + Number(row.km_carro || 0),
      num_geladeiras: acc.num_geladeiras + Number(row.num_geladeiras || 0),
    }), {
      massa_kg: 0,
      mat_recuperado: 0,
      energia_gerada: 0,
      co2_evitado: 0,
      espaco_aterro: 0,
      km_carro: 0,
      num_geladeiras: 0,
    });

    // Aggregate previous period totals
    const previousTotals = (previousPeriodData || []).reduce((acc, row) => ({
      massa_kg: acc.massa_kg + Number(row.massa_kg || 0),
      mat_recuperado: acc.mat_recuperado + Number(row.mat_recuperado || 0),
      energia_gerada: acc.energia_gerada + Number(row.energia_gerada || 0),
      co2_evitado: acc.co2_evitado + Number(row.co2_evitado || 0),
      espaco_aterro: acc.espaco_aterro + Number(row.espaco_aterro || 0),
      km_carro: acc.km_carro + Number(row.km_carro || 0),
      num_geladeiras: acc.num_geladeiras + Number(row.num_geladeiras || 0),
    }), {
      massa_kg: 0,
      mat_recuperado: 0,
      energia_gerada: 0,
      co2_evitado: 0,
      espaco_aterro: 0,
      km_carro: 0,
      num_geladeiras: 0,
    });

    // Generate REAL KPIs with comparison
    const kpis: ImpactKPI[] = [
      { 
        id: 'co2', 
        label: 'CO₂ evitado', 
        value: Math.round(totals.co2_evitado * 100) / 100, 
        unit: 'kg', 
        icon: 'cloud',
        change: calculateChange(totals.co2_evitado, previousTotals.co2_evitado),
        tooltip: 'CO₂ evitado calculado com base nas fórmulas de impacto por tipo de material.'
      },
      { 
        id: 'km', 
        label: 'Equivalente em km rodados', 
        value: Math.round(totals.km_carro), 
        unit: 'km', 
        icon: 'car',
        change: calculateChange(totals.km_carro, previousTotals.km_carro),
        tooltip: 'Equivalência calculada: CO₂ evitado ÷ 0.120 kg CO₂/km.'
      },
      { 
        id: 'landfill', 
        label: 'Espaço de aterro evitado', 
        value: Math.round(totals.espaco_aterro * 1000) / 1000, 
        unit: 'm³', 
        icon: 'trash',
        change: calculateChange(totals.espaco_aterro, previousTotals.espaco_aterro),
        tooltip: 'Volume de aterro sanitário evitado com a destinação correta.'
      },
      { 
        id: 'energy', 
        label: 'Energia gerada', 
        value: Math.round(totals.energia_gerada * 100) / 100, 
        unit: 'kWh', 
        icon: 'zap',
        change: calculateChange(totals.energia_gerada, previousTotals.energia_gerada),
        tooltip: 'Energia recuperada de rejeitos e óleo usado.'
      },
      { 
        id: 'recovered', 
        label: 'Material recuperado', 
        value: Math.round(totals.mat_recuperado * 100) / 100, 
        unit: 'kg', 
        icon: 'recycle',
        change: calculateChange(totals.mat_recuperado, previousTotals.mat_recuperado),
        tooltip: 'Material efetivamente recuperado para reciclagem ou compostagem.'
      },
      { 
        id: 'fridges', 
        label: 'Geladeiras de aterro evitadas', 
        value: Math.round(totals.num_geladeiras * 100) / 100, 
        unit: 'un', 
        icon: 'refrigerator',
        change: calculateChange(totals.num_geladeiras, previousTotals.num_geladeiras),
        tooltip: 'Volume equivalente em geladeiras (0.6 m³ cada).'
      },
    ];

    // Calculate material summary grouped by the 5 chart categories (REAL DATA)
    const categoryMap = new Map<string, { massa: number; co2: number; energia: number; recuperado: number }>();
    impactData.forEach(row => {
      const kindRaw = (row.kind_of_material || '').toLowerCase();
      const category = KIND_OF_MATERIAL_TO_CATEGORY[kindRaw];
      if (!category) return; // ignorar materiais fora das 5 categorias

      const massa = Number(row.massa_kg || 0);
      if (!massa || massa <= 0 || !isFinite(massa)) return;

      const current = categoryMap.get(category) || { massa: 0, co2: 0, energia: 0, recuperado: 0 };
      categoryMap.set(category, {
        massa: current.massa + massa,
        co2: current.co2 + Number(row.co2_evitado || 0),
        energia: current.energia + Number(row.energia_gerada || 0),
        recuperado: current.recuperado + Number(row.mat_recuperado || 0),
      });
    });

    const materialSummary: MaterialSummary[] = Array.from(categoryMap.entries())
      .map(([category, data]) => {
        const config = MATERIAL_CHART_CATEGORIES[category];
        return {
          material: config.label,
          weight: data.massa,
          percentage: totals.massa_kg > 0 ? (data.massa / totals.massa_kg) * 100 : 0,
          icon: 'recycle',
          color: config.color,
        };
      })
      .sort((a, b) => {
        const orderA = MATERIAL_CHART_CATEGORIES[a.material]?.order ?? 99;
        const orderB = MATERIAL_CHART_CATEGORIES[b.material]?.order ?? 99;
        return orderA - orderB;
      });

    // Impact by material type (REAL DATA)
    const impactByMaterial: ImpactByMaterial[] = Array.from(categoryMap.entries())
      .filter(([_, data]) => data.massa > 0)
      .map(([category, data]) => ({
        material: MATERIAL_CHART_CATEGORIES[category].label,
        co2: Math.round(data.co2),
        energy: Math.round(data.energia),
        recovered: Math.round(data.recuperado),
      }))
      .sort((a, b) => b.co2 - a.co2);

    // Calculate evolution data by month (REAL DATA)
    // Filtrar dados para garantir que estão dentro do período selecionado
    const filterStart = startOfMonth(filters.dateRange.from);
    const filterEnd = endOfMonth(filters.dateRange.to);
    
    const monthMap = new Map<string, { co2: number; energia: number; material: number; aterro: number }>();
    impactData.forEach(row => {
      const date = parseISO(row.planned_date);
      // Verificar se a data está dentro do período selecionado
      if (isBefore(date, filterStart) || isAfter(date, filterEnd)) {
        return; // Ignorar dados fora do período
      }
      const monthKey = format(date, 'yyyy-MM');
      const current = monthMap.get(monthKey) || { co2: 0, energia: 0, material: 0, aterro: 0 };
      monthMap.set(monthKey, {
        co2: current.co2 + Number(row.co2_evitado || 0),
        energia: current.energia + Number(row.energia_gerada || 0),
        material: current.material + Number(row.mat_recuperado || 0),
        aterro: current.aterro + Number(row.espaco_aterro || 0),
      });
    });

    // Create ordered months for the filtered date range
    // Mostrar todos os meses do intervalo, mesmo que não tenham dados
    const evolutionData: EvolutionDataPoint[] = [];
    const monthsInRange = eachMonthOfInterval({
      start: filterStart,
      end: filterEnd
    });
    
    monthsInRange.forEach(date => {
      const monthKey = format(date, 'yyyy-MM');
      const monthLabel = format(date, 'MMM', { locale: ptBR });
      const data = monthMap.get(monthKey) || { co2: 0, energia: 0, material: 0, aterro: 0 };
      
      // Adicionar todos os meses do intervalo, mesmo que não tenham dados
      // Isso garante que o gráfico mostre o período completo selecionado
      evolutionData.push({
        period: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
        co2: Math.round(data.co2),
        energia: Math.round(data.energia),
        material: Math.round(data.material),
        aterro: Math.round(data.aterro * 1000) / 1000,
      });
    });

    // Calculate unit ranking (REAL DATA)
    const unitMap = new Map<string, { massa: number; co2: number }>();
    impactData.forEach(row => {
      const unitName = row.establishment_name || 'Não identificado';
      const current = unitMap.get(unitName) || { massa: 0, co2: 0 };
      unitMap.set(unitName, {
        massa: current.massa + Number(row.massa_kg || 0),
        co2: current.co2 + Number(row.co2_evitado || 0),
      });
    });

    const unitRanking: UnitRanking[] = Array.from(unitMap.entries())
      .map(([unit, data]) => ({ unit, value: data.massa, co2: data.co2 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

      return {
        totalWeight: totals.massa_kg,
        materialSummary,
        kpis,
        evolutionData,
        impactByMaterial,
        unitRanking,
      };
    } catch (error) {
      console.error('❌ Error processing impact data:', error);
      // Return empty data structure on error
      return {
        totalWeight: 0,
        materialSummary: [],
        kpis: [],
        evolutionData: [],
        impactByMaterial: [],
        unitRanking: [],
      };
    }
  }, [impactData, previousPeriodData, calculateChange, filters.dateRange]);

  // Filtrar meses fechados excluindo o mês atual
  const availableClosedMonths = useMemo(() => {
    const currentMonth = startOfMonth(new Date());
    return closedMonths.filter(month => !isSameMonth(month, currentMonth));
  }, [closedMonths]);

  // Atalhos de data usando apenas meses fechados disponíveis (excluindo mês atual)
  const dateShortcuts = useMemo(() => {
    if (availableClosedMonths.length === 0) {
      return [];
    }

    const shortcuts = [];
    
    // Mês atual (último mês fechado disponível)
    const lastAvailableMonth = availableClosedMonths[availableClosedMonths.length - 1];
    if (lastAvailableMonth) {
      shortcuts.push({
        label: 'Mês atual',
        getValue: () => ({
          from: startOfMonth(lastAvailableMonth),
          to: endOfMonth(lastAvailableMonth)
        })
      });
    }

    // Últimos 3 meses fechados
    if (availableClosedMonths.length >= 3) {
      const last3Months = availableClosedMonths.slice(-3);
      shortcuts.push({
        label: 'Últimos 3 meses',
        getValue: () => {
          const from = startOfMonth(last3Months[0]);
          const to = endOfMonth(last3Months[last3Months.length - 1]);
          return { from, to };
        }
      });
    }

    // Últimos 6 meses fechados
    if (availableClosedMonths.length >= 6) {
      const last6Months = availableClosedMonths.slice(-6);
      shortcuts.push({
        label: 'Últimos 6 meses',
        getValue: () => {
          const from = startOfMonth(last6Months[0]);
          const to = endOfMonth(last6Months[last6Months.length - 1]);
          return { from, to };
        }
      });
    }

    return shortcuts;
  }, [availableClosedMonths]);

  const updateFilters = (newFilters: Partial<ImpactFilters>) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      
      // Validar dateRange se foi alterado
      if (newFilters.dateRange && closedMonths.length > 0) {
        const isValid = validateDateRange(updated.dateRange.from, updated.dateRange.to);
        if (!isValid && availableClosedMonths.length > 0) {
          // Se o range não é válido, usar o último mês fechado disponível
          const lastAvailable = availableClosedMonths[availableClosedMonths.length - 1];
          if (lastAvailable) {
            updated.dateRange = {
              from: startOfMonth(lastAvailable),
              to: endOfMonth(lastAvailable)
            };
          }
        }
      }
      
      return updated;
    });
  };

  const resetFilters = () => {
    setFilters(getDefaultFilters());
  };

  const establishmentOptions = filterEstablishments.map(e => ({
    value: e.establishment_id,
    label: e.establishment_name,
  }));

  // Waste type options for the filter
  const wasteTypeOptions = WASTE_TYPE_OPTIONS;

  return {
    filters,
    updateFilters,
    resetFilters,
    dateShortcuts,
    data,
    isLoading,
    establishmentOptions,
    materialOptions,
    wasteTypeOptions,
    closedMonths,
    validateDateRange,
  };
};
